import React, { useEffect, useState } from 'react';
import type { AgentEmailDraft } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { btn, input } from './agentUi';

interface Props {
  draft: AgentEmailDraft;
  canEdit: boolean;
  onChanged: (draft: AgentEmailDraft) => void;
}

const AUDIENCE: Record<string, string> = { insured: 'To the insured', broker: 'To a broker', underwriter: 'To an underwriter', internal: 'Internal' };

const EmailDraftEditor: React.FC<Props> = ({ draft, canEdit, onChanged }) => {
  const [to, setTo] = useState(draft.toAddresses.join(', '));
  const [cc, setCc] = useState(draft.ccAddresses.join(', '));
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.bodyText || '');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setTo(draft.toAddresses.join(', '));
    setCc(draft.ccAddresses.join(', '));
    setSubject(draft.subject || '');
    setBody(draft.bodyText || '');
  }, [draft.id, draft.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = canEdit && draft.status === 'draft';
  const dirty = to !== draft.toAddresses.join(', ') || cc !== draft.ccAddresses.join(', ') || subject !== (draft.subject || '') || body !== (draft.bodyText || '');

  const save = async (): Promise<AgentEmailDraft | null> => {
    setBusy('save');
    setError(null);
    try {
      const { draft: saved } = await agentApi.updateDraft(draft.id, { toAddresses: to.split(/[,;]/).map((s) => s.trim()).filter(Boolean), ccAddresses: cc.split(/[,;]/).map((s) => s.trim()).filter(Boolean), subject, bodyText: body });
      onChanged(saved);
      setNotice('Saved.');
      return saved;
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the draft'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    setBusy('discard');
    try {
      const { draft: d } = await agentApi.discardDraft(draft.id);
      onChanged(d);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not discard'));
    } finally {
      setBusy(null);
    }
  };

  // The agent never emails clients (David, 2026-10-07): the user sends drafts themselves.
  const mailto = `mailto:${encodeURIComponent(to.replace(/\s+/g, ''))}?${[cc.trim() ? `cc=${encodeURIComponent(cc.replace(/\s+/g, ''))}` : '', `subject=${encodeURIComponent(subject)}`, `body=${encodeURIComponent(body)}`].filter(Boolean).join('&')}`;

  const copy = () => {
    navigator.clipboard?.writeText(`To: ${to}\n${cc ? `Cc: ${cc}\n` : ''}Subject: ${subject}\n\n${body}`).then(() => setNotice('Copied to clipboard.'));
  };

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{AUDIENCE[draft.audience] || draft.audience}</span>
        <span className={draft.status === 'sent' ? 'text-green-600 font-medium' : draft.status === 'discarded' ? 'line-through' : ''}>
          {draft.status === 'draft' ? 'Draft — not sent' : draft.status}
        </span>
      </div>
      <label className="block">
        <span className="text-xs text-gray-500">To</span>
        <input className={input} value={to} onChange={(e) => setTo(e.target.value)} disabled={!editable} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Cc</span>
        <input className={input} value={cc} onChange={(e) => setCc(e.target.value)} disabled={!editable} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Subject</span>
        <input className={input} value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!editable} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Body</span>
        <textarea className={`${input} font-sans`} rows={10} value={body} onChange={(e) => setBody(e.target.value)} disabled={!editable} />
      </label>
      {draft.attachments.length > 0 && (
        <div className="text-xs text-gray-600 dark:text-gray-300">
          Attachments: {draft.attachments.map((a) => a.filename).join(', ')}
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {notice && !error && <p className="text-xs text-green-700 dark:text-green-400">{notice}</p>}
      <div className="flex flex-wrap gap-2 pt-1">
        <button className={btn.secondary} onClick={copy}>Copy</button>
        <a className={btn.primary} href={mailto} title="Opens a new message in your own email client — RiskNinja never sends it">Open in my email</a>
        {editable && (
          <>
            <button className={btn.secondary} onClick={save} disabled={!dirty || !!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            <button className={btn.danger} onClick={discard} disabled={!!busy}>Discard</button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailDraftEditor;
